import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

type CreateDeliveryNoteVestigasFormatResponse = {
  code: number;
  id: UUID;
  requestId: UUID | undefined;
};

type CreateDeliveryNoteVestigasFormatPayload = Record<string, unknown>;

/**
 * Creates a delivery note in the Vestigas format.
 *
 * @param deliveryNoteData - An object containing the delivery note details to be created.
 * @returns A promise that resolves with the API response containing the created delivery note ID and request status.
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/create_delivery_note_vestigas_format_asset_vestigas_dln_post
 */
export const createDeliveryNoteVestigasFormat = async (
  deliveryNoteData: CreateDeliveryNoteVestigasFormatPayload,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.DELIVERY_NOTE.CREATE_VESTIGAS_FORMAT(), {
      json: deliveryNoteData,
    })
    .json<CreateDeliveryNoteVestigasFormatResponse>();

  return response;
};

/**
 * Custom hook for creating a delivery note in the Vestigas format.
 *
 * @returns An object containing the mutation function and additional properties.
 */
export const useMutationCreateDeliveryNoteVestigasFormat = (
  options?: UseMutationOptions,
) => {
  return useMutation({
    mutationFn: async (
      deliveryNoteData: CreateDeliveryNoteVestigasFormatPayload,
    ) => createDeliveryNoteVestigasFormat(deliveryNoteData),

    onSuccess() {
      ToastService.success(['Lieferung erstellt.']);
    },
    ...options,
  });
};
